"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ArrowLeft, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react"
import { useRouter } from "next/navigation"
import { useLanguage } from "@/app/contexts/language-context"

export default function ResearchManagementSystemPage() {
  const router = useRouter()
  const { t, language } = useLanguage()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  // Start autoplay by default per request
  const [isPlaying, setIsPlaying] = useState(true)
  const [syncLogs, setSyncLogs] = useState(1024)

  const latchTransition = { type: "spring" as const, stiffness: 400, damping: 30, restDelta: 0.001 }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setSyncLogs(prev => prev + Math.floor(Math.random() * 12))
    }, 400)
    return () => clearInterval(interval)
  }, [])

  const projectImages = [
    {
      src: "/projects/research-management-system/research-dashboard.png",
      alt: "Research Management System Dashboard",
      fallbackTitle: "Main Dashboard",
      fallbackDescription: "Comprehensive dashboard with research metrics, project tracking, and analytics",
    },
    {
      src: "/projects/research-management-system/research-kanban.png",
      alt: "Project Management Interface",
      fallbackTitle: "Project Management",
      fallbackDescription: "Intuitive project management with kanban boards and milestone tracking",
    },
    {
      src: "/projects/research-management-system/research-collaboration-communication.png",
      alt: "Collaboration Tools",
      fallbackTitle: "Team Collaboration",
      fallbackDescription: "Advanced collaboration tools for research teams and stakeholders",
    },
    {
      src: "/projects/research-management-system/research-analytics.png",
      alt: "Data Analysis Interface",
      fallbackTitle: "Data Analysis",
      fallbackDescription: "Powerful data analysis tools with interactive charts and reporting",
    },
    {
      src: "/projects/research-management-system/research-publication-management.png",
      alt: "Publication Management",
      fallbackTitle: "Publication Management",
      fallbackDescription: "Streamlined publication tracking and manuscript management system",
    },
    {
      src: "/projects/research-management-system/research-mobile-ux.png",
      alt: "Mobile Interface",
      fallbackTitle: "Mobile Experience",
      fallbackDescription: "Fully responsive mobile interface for research management on the go",
    },
  ]

  const processSteps = [
    {
      step: "01",
      fallbackTitle: "Requirements Analysis",
      fallbackDescription:
        "Comprehensive analysis of research workflow requirements, stakeholder interviews, and system architecture planning.",
      tools: ["Figma", "Miro", "User Interviews"],
    },
    {
      step: "02",
      fallbackTitle: "System Design",
      fallbackDescription:
        "Database design, API architecture, and user interface wireframing with focus on research-specific workflows.",
      tools: ["Next.js", "PostgreSQL", "Prisma"],
    },
    {
      step: "03",
      fallbackTitle: "Frontend Development",
      fallbackDescription:
        "Modern React-based frontend with responsive design, real-time updates, and intuitive user experience.",
      tools: ["React", "TypeScript", "Tailwind CSS"],
    },
    {
      step: "04",
      fallbackTitle: "Backend Implementation",
      fallbackDescription:
        "Robust backend API development with authentication, data validation, and research-specific business logic.",
      tools: ["Node.js", "Express", "JWT"],
    },
    {
      step: "05",
      fallbackTitle: "Integration & Testing",
      fallbackDescription: "System integration, automated testing, performance optimization, and security implementation.",
      tools: ["Jest", "Cypress", "GitHub Actions"],
    },
    {
      step: "06",
      fallbackTitle: "Deployment & Monitoring",
      fallbackDescription: "Production deployment with monitoring, analytics, and continuous integration/deployment setup.",
      tools: ["Vercel", "Sentry", "Google Analytics"],
    },
  ]

  const projectSpecs = {
    client: "Academic Research Institute",
    duration: "8 weeks",
    deliverables: "Full-stack Web Application",
    technology: "Next.js, TypeScript, PostgreSQL",
    deployment: "Vercel Platform",
    scope: "Research Project Management System",
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % projectImages.length)
  }

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + projectImages.length) % projectImages.length)
  }

  const toggleSlideshow = () => {
    setIsPlaying(!isPlaying)
  }

  // Auto-play slideshow
  useEffect(() => {
    let slideshowInterval: NodeJS.Timeout | null = null
    if (isPlaying) {
      slideshowInterval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % projectImages.length)
      }, 30000) // Change image every 30 seconds
    }
    return () => {
      if (slideshowInterval) {
        clearInterval(slideshowInterval)
      }
    }
  }, [isPlaying, projectImages.length])

  return (
    <>
  <main className="main-content pb-16">
        {/* Hero Section */}
        <div className="relative h-[70vh] min-h-[500px] w-full bg-gradient-to-br from-blue-900 to-purple-800 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 opacity-50">
            {/* Prefer WebP variants when available. Fallback to original image if WebP not supported. */}
            {(() => {
              const img = projectImages[currentImageIndex]
              const src = img.src || "/placeholder.svg"
              const base = src.replace(/\.(png|jpe?g)$/i, "")
              const webpOriginal = `${base}.original.webp`
              const webp1024 = `${base}-1024.webp`
              const webp640 = `${base}-640.webp`
              return (
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${webp640} 640w, ${webp1024} 1024w, ${webpOriginal} 2048w`}
                    sizes="(max-width: 640px) 640px, (max-width: 1024px) 1024px, 2048px"
                  />
                  <img
                    src={src}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const t = e.currentTarget as HTMLImageElement
                      t.onerror = null
                      t.src = "/project-management-kanban.png"
                    }}
                  />
                </picture>
              )
            })()}
          </div>

          {/* HUD: Audit Trail */}
          <div className="absolute top-8 left-8 z-20 bg-slate-900/90 p-4 border-l-2 border-sky-500 backdrop-blur-md pointer-events-none">
            <span className="text-[10px] font-mono text-sky-400/70 uppercase tracking-widest block mb-2">Audit_Trail_Active</span>
            <div className="flex flex-col gap-1 text-xs font-mono">
              <div className="flex justify-between gap-8 text-slate-300"><span>Data_Silos:</span> <span className="text-emerald-400">0</span></div>
              <div className="flex justify-between gap-8 text-slate-300">
                <span>Nodes_Synced:</span>
                <motion.span
                  key={syncLogs}
                  initial={{ opacity: 0.5, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={latchTransition}
                  className="text-sky-400"
                >
                  {syncLogs}
                </motion.span>
              </div>
            </div>
          </div>

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>

          {/* Navigation Controls */}
          <div className="absolute inset-0 flex items-center justify-between px-6 z-10">
            <Button
              variant="ghost"
              size="icon"
              className="text-white bg-black/20 hover:bg-black/40 backdrop-blur-sm"
              onClick={prevImage}
              aria-label={t('projects.detail.prevImage') || 'Previous image'}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white bg-black/20 hover:bg-black/40 backdrop-blur-sm"
              onClick={nextImage}
              aria-label={t('projects.detail.nextImage') || 'Next image'}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>

          {/* Content */}
          <div className="container mx-auto px-6 relative z-10 h-full flex flex-col justify-end pb-12">
            <Button
              variant="ghost"
              size="sm"
              className="text-white mb-6 w-fit hover:bg-white/20"
              onClick={() => router.back()}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('projects.detail.back')}
            </Button>

            <Badge className="mb-4 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 w-fit">
              {t('projects.research-management-system.badge') || 'Full-Stack Web Development'}
            </Badge>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-4xl">
              {t('projects.research-management-system.title') || 'Research Management System'}
            </h1>

            <p className="text-xl text-white/80 mb-6 max-w-3xl">
              {t('projects.research-management-system.description') || 'A comprehensive web application for managing research projects, collaboration, and data analysis with modern UI/UX design'}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-white/80 mb-6">
              <span>{t('projects.detail.specLabels.client')} {projectSpecs.client}</span>
              <span>•</span>
              <span>{t('projects.detail.specLabels.duration')} {projectSpecs.duration}</span>
              <span>•</span>
              <span>{projectSpecs.deliverables}</span>
            </div>

            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white/30 hover:bg-white/20 bg-transparent"
                onClick={() => window.open('https://research-hub.davidxavila.com', '_blank')}
              >
                {t('projects.detail.viewResearchHub') || '🚀 View the Live ResearchHub Demo →'}
              </Button>

              <div className="text-sm text-white/60">
                {currentImageIndex + 1} / {projectImages.length}
              </div>
            </div>
          </div>

          {/* Image Indicators */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
            {projectImages.map((_, index) => (
              <button
                key={index}
                aria-label={`Go to image ${index + 1}`}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex ? "bg-white" : "bg-white/40"
                }`}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </div>
        </div>

        {/* Project Details */}
        <div className="container mx-auto px-6 py-16">
          <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-12">
              <TabsTrigger value="overview">{t('projects.detail.tabs.overview')}</TabsTrigger>
              <TabsTrigger value="gallery">{t('projects.detail.tabs.gallery')}</TabsTrigger>
              <TabsTrigger value="process">{t('projects.detail.tabs.process')}</TabsTrigger>
              <TabsTrigger value="specs">{t('projects.detail.tabs.specs')}</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-12">
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                <h2 className="text-3xl font-bold mb-6">{t('projects.detail.overview')}</h2>
                  <div className="prose prose-lg dark:prose-invert">
                    <p>{t('projects.research-management-system.overview.p1')}</p>
                    <p>{t('projects.research-management-system.overview.p2')}</p>
                    <p>{t('projects.research-management-system.overview.p3')}</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-6">{t('projects.detail.keyFeatures')}</h3>
                  <div className="space-y-4">
                    {([1, 2, 3, 4] as number[]).map((num) => {
                      const title = t(`projects.research-management-system.features.${num}.title`) || ''
                      const description = t(`projects.research-management-system.features.${num}.description`) || ''
                      return (
                        <div key={num} className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <h4 className="font-semibold">{title}</h4>
                            <p className="text-medium-contrast">{description}</p>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold mb-4">{t('projects.detail.technologyStack') || 'Technology Stack'}</h3>
                    <div className="flex flex-wrap gap-2">
                      {["Next.js", "TypeScript", "React", "Tailwind CSS", "PostgreSQL", "Prisma", "Vercel"].map(
                        (tech) => (
                          <Badge key={tech} variant="outline" className="text-sm">
                            {tech}
                          </Badge>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="gallery" className="space-y-8">
              <h2 className="text-3xl font-bold text-center mb-12">{t('projects.detail.gallery')}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projectImages.map((image, index) => (
                  <Card key={index} className="group overflow-hidden hover:shadow-xl transition-all duration-300">
                    <div className="relative overflow-hidden">
                      {(() => {
                        const src = image.src || "/placeholder.svg"
                        const base = src.replace(/\.(png|jpe?g)$/i, "")
                        const webpOriginal = `${base}.original.webp`
                        const webp1024 = `${base}-1024.webp`
                        const webp640 = `${base}-640.webp`
                        return (
                          <picture>
                            <source
                              type="image/webp"
                              srcSet={`${webp640} 640w, ${webp1024} 1024w, ${webpOriginal} 2048w`}
                              sizes="(max-width: 640px) 640px, (max-width: 1024px) 1024px, 2048px"
                            />
                            <img
                              src={src}
                              alt={image.alt}
                              className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                const t = e.currentTarget as HTMLImageElement
                                t.onerror = null
                                t.src = "/project-management-kanban.png"
                              }}
                            />
                          </picture>
                        )
                      })()}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white bg-black/20 hover:bg-black/40 backdrop-blur-sm"
                          onClick={() =>
                            window.open("https://research-hub.davidxavila.com", "_blank")
                          }
                          aria-label={t('projects.detail.openLiveDemo') || 'Open live demo (opens in new tab)'}
                        >
                          <ExternalLink className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2">{t(`projects.research-management-system.gallery.${index}.title`) || image.fallbackTitle}</h3>
                      <p className="text-sm text-medium-contrast">{t(`projects.research-management-system.gallery.${index}.description`) || image.fallbackDescription}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="process" className="space-y-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t('projects.detail.process')}</h2>
                <p className="text-xl text-medium-contrast max-w-3xl mx-auto">
                  {t('projects.research-management-system.processIntro') || 'A systematic approach to building a comprehensive research management platform'}
                </p>
              </div>

              <div className="space-y-8">
                {processSteps.map((step, index) => {
                  const stepIndex = index + 1
                  const title = t(`projects.research-management-system.process.${stepIndex}.title`) || (step as any).fallbackTitle || ''
                  const description = t(`projects.research-management-system.process.${stepIndex}.description`) || (step as any).fallbackDescription || ''
                  return (
                    <div key={index} className="flex gap-8 items-start">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {step.step}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-3">{title}</h3>
                        <p className="text-medium-contrast mb-4 text-lg">{description}</p>
                        <div className="flex flex-wrap gap-2">
                          {step.tools.map((tool) => (
                            <Badge key={tool} variant="outline" className="text-sm">
                              {tool}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </TabsContent>

            <TabsContent value="specs" className="space-y-8">
              <h2 className="text-3xl font-bold text-center mb-12">{t('projects.detail.specs')}</h2>

              <div className="grid md:grid-cols-2 gap-8">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-6">{t('projects.detail.projectDetails')}</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.client')}</span>
                        <span className="font-medium">{projectSpecs.client}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.duration')}</span>
                        <span className="font-medium">{projectSpecs.duration}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.deliverables')}</span>
                        <span className="font-medium">{projectSpecs.deliverables}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.technology')}</span>
                        <span className="font-medium">{projectSpecs.technology}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.deployment')}</span>
                        <span className="font-medium">{projectSpecs.deployment}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.scope')}</span>
                        <span className="font-medium">{projectSpecs.scope}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-6">{t('projects.detail.technicalSpecifications')}</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">{t('projects.detail.specSub.frontendFramework') || 'Frontend Framework'}</h4>
                        <p className="text-medium-contrast">Next.js 14 with React 18 and TypeScript</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">{t('projects.detail.specSub.database') || 'Database'}</h4>
                        <p className="text-medium-contrast">PostgreSQL with Prisma ORM for data management</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">{t('projects.detail.specSub.styling') || 'Styling'}</h4>
                        <p className="text-medium-contrast">Tailwind CSS with shadcn/ui components</p>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">{t('projects.detail.specSub.deployment') || 'Deployment'}</h4>
                        <p className="text-medium-contrast">Vercel platform with automatic CI/CD</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="text-center mt-12">
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                      className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                      onClick={() => window.open("https://research-hub.davidxavila.com", "_blank")}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t('projects.detail.viewLive')}
                    </Button>
                    {/* View Source removed per request */}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 py-16">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">{t('projects.detail.ctaTitle')}</h2>
            <p className="text-xl text-medium-contrast mb-8 max-w-2xl mx-auto">
              {t('projects.detail.ctaDescription')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                onClick={() => router.push("/#contact")}
              >
                {t('projects.detail.deployResearch') || 'Deploy Research Protocol'}
              </Button>
              <Button size="lg" variant="outline" onClick={() => router.push("/#case-studies")}>
                {t('projects.detail.viewMoreProjects') || 'View More Projects'}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
